import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API } from '../lib/api';

const markerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const DEFAULT_CENTER = [40.4168, -3.7038];

const parseCoordinate = (value, min, max) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < min || parsed > max) {
    return null;
  }

  return parsed;
};

const MapCenterUpdater = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (Array.isArray(center) && center.length === 2) {
      map.setView(center, 14, { animate: false });
      setTimeout(() => map.invalidateSize(), 0);
    }
  }, [center, map]);

  return null;
};

export const OSMMap = ({
  address,
  latitude,
  longitude,
  markerLabel = 'Ubicacion del alojamiento',
  heightClassName = 'h-96'
}) => {
  const [resolvedPosition, setResolvedPosition] = useState(null);
  const [isResolving, setIsResolving] = useState(false);

  const numericLatitude = useMemo(() => parseCoordinate(latitude, -90, 90), [latitude]);

  const numericLongitude = useMemo(() => parseCoordinate(longitude, -180, 180), [longitude]);

  useEffect(() => {
    let cancelled = false;

    if (numericLatitude !== null && numericLongitude !== null) {
      setResolvedPosition([numericLatitude, numericLongitude]);
      return () => {
        cancelled = true;
      };
    }

    if (!address || address.trim().length < 3) {
      setResolvedPosition(null);
      return () => {
        cancelled = true;
      };
    }

    const controller = new AbortController();
    const query = address.trim();

    const resolveAddress = async () => {
      try {
        setIsResolving(true);
        const params = new URLSearchParams({
          q: query
        });
        const response = await fetch(`${API}/geocode?${params.toString()}`, {
          signal: controller.signal,
          headers: {
            Accept: 'application/json'
          }
        });
        if (!response.ok) {
          return;
        }

        const data = await response.json();
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          const item = data[0];
          const lat = Number(item.lat);
          const lon = Number(item.lon);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            setResolvedPosition([lat, lon]);
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error resolving map address:', error);
        }
      } finally {
        if (!cancelled) {
          setIsResolving(false);
        }
      }
    };

    const timeoutId = setTimeout(resolveAddress, 450);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [address, numericLatitude, numericLongitude]);

  const center = resolvedPosition || DEFAULT_CENTER;

  if (!resolvedPosition && isResolving) {
    return (
      <div className={`w-full ${heightClassName} rounded-lg border bg-gray-50 flex items-center justify-center text-gray-600`}>
        Buscando ubicacion en el mapa...
      </div>
    );
  }

  if (!resolvedPosition) {
    const mapsUrl = address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
      : null;
    const embedUrl = address
      ? `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=14&output=embed`
      : null;

    if (embedUrl) {
      return (
        <div className={`w-full ${heightClassName} rounded-lg overflow-hidden border bg-gray-50`}>
          <iframe
            title="Mapa de ubicacion"
            src={embedUrl}
            className="w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      );
    }

    return (
      <div className={`w-full ${heightClassName} rounded-lg border bg-gray-50 flex flex-col items-center justify-center gap-2 text-gray-600`}>
        <span>No se pudo cargar el mapa interactivo.</span>
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline text-sm"
          >
            Ver en Google Maps →
          </a>
        )}
      </div>
    );
  }

  return (
    <div className={`w-full ${heightClassName} rounded-lg overflow-hidden border isolate`}>
      <MapContainer center={center} zoom={14} className="h-full w-full" scrollWheelZoom={false}>
        <MapCenterUpdater center={center} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={center} icon={markerIcon}>
          <Popup>{markerLabel}</Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};
